// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * Turns a snapshot comparison into something an owner can read.
 *
 * The comparison table already says what every cost code did. It is not a
 * narrative: forty rows of deltas is a spreadsheet, and the conversation it has
 * to support — "why is this four hundred thousand dollars more than you told me
 * in March?" — is the most painful one in preconstruction.
 *
 * What makes it a narrative is leaving things out. A handful of changes almost
 * always account for nearly all of the movement, and the rest is rounding that
 * buries the point. So this ranks by size, keeps enough to account for the
 * movement, and collapses the tail into a single line.
 *
 * It carries the estimator's own notes as the WHY, and reports how many
 * material changes have no explanation yet, because a number without a reason
 * is what started the argument in the first place.
 *
 * Nothing here decides to send anything. The output is a draft.
 */

export interface VersionSummary {
  [costCode: string]: { code: string; label: string; total: number };
}

export type ChangeKind = "added" | "removed" | "increased" | "decreased";

export interface NarrativeLine {
  code: string;
  /** Scope name with the code prefix and division suffix stripped off. */
  scope: string;
  division: string;
  from: number;
  to: number;
  delta: number;
  /** Null where there is nothing to be a percentage of. */
  pct: number | null;
  kind: ChangeKind;
  /** The estimator's explanation. Empty is a gap to fill, not a reason. */
  note: string;
}

export interface ChangeNarrative {
  fromTotal: number;
  toTotal: number;
  delta: number;
  pct: number | null;
  /** Gross movement in each direction, which net change hides. */
  increases: number;
  decreases: number;
  lines: NarrativeLine[];
  omitted: { count: number; net: number };
  /** Material lines with no explanation written yet. */
  missingNotes: number;
}

export interface NarrativeOptions {
  /** Keep lines until this much of the gross movement is accounted for. */
  coverage?: number;
  /** Never list more than this, however the movement is spread. */
  maxLines?: number;
}

/** "05-050 — Structural Steel (Div 05)" → "Structural Steel". */
export function scopeName(label: string, code: string): string {
  const stripped = String(label || "")
    // The separator must have whitespace around it. Without that the hyphen
    // inside the code matches and "07-200" is stripped to "200".
    .replace(/^\S+\s+[—–-]\s+/, "")
    .replace(/\s*\([^)]*\)\s*$/, "")
    .trim();
  return stripped || code;
}

/** "05-050 — Structural Steel (Div 05)" → "Div 05", falling back to the code. */
export function divisionOf(label: string, code: string): string {
  const m = String(label || "").match(/\((Div\s*\d+)\)/i);
  if (m) return m[1];
  const prefix = String(code || "").split("-")[0];
  return prefix ? `Div ${prefix}` : "";
}

function kindOf(from: number, to: number): ChangeKind {
  if (from === 0) return "added";
  if (to === 0) return "removed";
  return to > from ? "increased" : "decreased";
}

export function buildChangeNarrative(
  left: VersionSummary,
  right: VersionSummary,
  noteFor: (code: string) => string,
  opts: NarrativeOptions = {},
): ChangeNarrative {
  const coverage = opts.coverage ?? 0.9;
  const maxLines = opts.maxLines ?? 8;

  const codes = Array.from(new Set([...Object.keys(left || {}), ...Object.keys(right || {})]));

  const all: NarrativeLine[] = [];
  let fromTotal = 0;
  let toTotal = 0;

  for (const code of codes) {
    const from = Number(left?.[code]?.total) || 0;
    const to = Number(right?.[code]?.total) || 0;
    fromTotal += from;
    toTotal += to;

    const delta = to - from;
    if (delta === 0) continue;   // a line that did not move is not a change

    const label = left?.[code]?.label || right?.[code]?.label || code;
    all.push({
      code,
      scope: scopeName(label, code),
      division: divisionOf(label, code),
      from, to, delta,
      pct: from !== 0 ? delta / Math.abs(from) : null,
      kind: kindOf(from, to),
      note: (noteFor(code) || "").trim(),
    });
  }

  // Ranked by size of movement, not by whether it went up. A big reduction is
  // as much of the story as a big increase, and often the answer to it.
  all.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));

  const gross = all.reduce((s, l) => s + Math.abs(l.delta), 0);
  const increases = all.filter((l) => l.delta > 0).reduce((s, l) => s + l.delta, 0);
  const decreases = all.filter((l) => l.delta < 0).reduce((s, l) => s + l.delta, 0);

  const lines: NarrativeLine[] = [];
  let running = 0;
  for (const line of all) {
    // Stop once the movement is accounted for. Measured against gross, not net:
    // a +500k and a -480k net to almost nothing, and a narrative that called
    // that a quiet month would be worse than useless.
    if (lines.length >= maxLines) break;
    if (gross > 0 && running / gross >= coverage) break;
    lines.push(line);
    running += Math.abs(line.delta);
  }

  const rest = all.slice(lines.length);

  return {
    fromTotal,
    toTotal,
    delta: toTotal - fromTotal,
    pct: fromTotal !== 0 ? (toTotal - fromTotal) / Math.abs(fromTotal) : null,
    increases,
    decreases,
    lines,
    omitted: {
      count: rest.length,
      net: rest.reduce((s, l) => s + l.delta, 0),
    },
    missingNotes: lines.filter((l) => !l.note).length,
  };
}

// ── Prose ────────────────────────────────────────────────────────────────────

const money = (n: number) => {
  const s = "$" + Math.abs(Math.round(n)).toLocaleString();
  return n < 0 ? `(${s})` : s;
};

/**
 * The opening paragraph, in the words an owner uses.
 *
 * Says the net change first because that is the question, then the gross either
 * way when they differ enough to matter — "up 400k" and "up 900k against 500k
 * of savings" are different conversations, and the second one is the one where
 * the estimator gets credit for the savings.
 */
export function narrativeSummary(n: ChangeNarrative, fromLabel: string, toLabel: string): string {
  if (n.delta === 0) {
    return `The estimate is unchanged between ${fromLabel} and ${toLabel} at ${money(n.toTotal)}.`;
  }
  const dir = n.delta > 0 ? "increased" : "decreased";
  const pct = n.pct != null ? ` (${n.pct > 0 ? "+" : ""}${(n.pct * 100).toFixed(1)}%)` : "";
  let s = `Between ${fromLabel} and ${toLabel} the estimate ${dir} from ${money(n.fromTotal)} `
        + `to ${money(n.toTotal)}, a change of ${money(n.delta)}${pct}.`;

  // Only worth saying when the offset is big enough to change the picture.
  const offset = Math.min(n.increases, Math.abs(n.decreases));
  if (offset > 0 && offset >= Math.abs(n.delta) * 0.2) {
    s += ` That is ${money(n.increases)} of increases against ${money(Math.abs(n.decreases))} of reductions.`;
  }
  return s;
}

/** One line of the body, as a sentence rather than a table row. */
export function lineSentence(l: NarrativeLine): string {
  const amount = money(Math.abs(l.delta));
  const head =
    l.kind === "added" ? `${l.scope} was added at ${amount}`
    : l.kind === "removed" ? `${l.scope} was removed, reducing the estimate by ${amount}`
    : l.kind === "increased" ? `${l.scope} increased by ${amount}`
    : `${l.scope} decreased by ${amount}`;
  const pct = l.pct != null && l.kind !== "added" && l.kind !== "removed"
    ? ` (${l.pct > 0 ? "+" : ""}${(l.pct * 100).toFixed(0)}%)`
    : "";
  return `${head}${pct}${l.note ? `. ${l.note.replace(/\.?$/, ".")}` : "."}`;
}

/** The tail, when there is one. */
export function omittedSentence(n: ChangeNarrative): string | null {
  if (n.omitted.count === 0) return null;
  const dir = n.omitted.net === 0 ? "offsetting each other"
    : n.omitted.net > 0 ? `adding ${money(n.omitted.net)}`
    : `reducing the estimate by ${money(Math.abs(n.omitted.net))}`;
  return `A further ${n.omitted.count} smaller ${n.omitted.count === 1 ? "change" : "changes"}, ${dir}.`;
}
